import React from 'react';
import { useSettings } from '../../lib/hooks/useSettings';
import { cn } from '../../lib/utils';

interface ConditionalIconProps {
  icon: React.ReactNode;
  type?: 'category' | 'status' | 'navigation' | 'general';
  fallbackText?: string;
  className?: string;
  showText?: boolean;
  children?: React.ReactNode;
}

export const ConditionalIcon: React.FC<ConditionalIconProps> = ({
  icon,
  type = 'general',
  fallbackText,
  className,
  showText: _showText = false,
  children
}) => {
  const { 
    shouldShowIcons,
    shouldShowCategoryIcons,
    shouldShowStatusIcons,
    shouldShowNavigationIcons
  } = useSettings();

  // Determine if this specific icon type should be shown
  const shouldShow = () => {
    if (!shouldShowIcons) return false;
    
    switch (type) {
      case 'category':
        return shouldShowCategoryIcons;
      case 'status':
        return shouldShowStatusIcons;
      case 'navigation':
        return shouldShowNavigationIcons;
      case 'general':
      default:
        return true;
    }
  };

  // If icons are disabled or this type shouldn't show, render fallback
  if (!shouldShow()) {
    if (fallbackText) {
      return <span className={cn("text-xs font-medium", className)}>{fallbackText}</span>;
    }
    if (children) {
      return <>{children}</>;
    }
    return null;
  }

  // Just return the icon as-is to avoid layout issues
  return <>{icon}</>;
};

export default ConditionalIcon; 
